import React, { useRef, useEffect } from 'react';
import { styled } from '@compiled/react';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useScrollState } from '@atlassian/jira-software-fast-virtual/src/services/use-scroll-state/index.tsx';
import { useMoreResultsStore } from '../../../services/more-results/index.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import type { FiltersState } from '../../../state/reducers/ui/filters/types.tsx';
import { getFilters } from '../../../state/selectors/filters/index.tsx';

/**
 * LastResultMarker should be placed after the last CardList with the last visible result (after filtering)
 * It will then track its position and update the moreResultsStore when it goes into and below the viewport.
 */
export const LastResultMarker = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Only use scrollElement as it wont update as much
	const { scrollElement } = useScrollState();
	const markerRef = useRef(null);
	const [, actions] = useMoreResultsStore();

	const filters = useBacklogSelector(getFilters);
	const filtersRef = useRef<FiltersState | null>(null);

	useEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (!window.IntersectionObserver) {
			return undefined;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;

				const isLastResultBelowFold =
					!entry.isIntersecting && entry.boundingClientRect.top > (entry.rootBounds?.bottom ?? 0);

				const isFilterApplied = Object.keys(filters).length > 0;
				const isFilterChanged = filters !== filtersRef.current;

				if (isLastResultBelowFold && isFilterChanged && isFilterApplied) {
					fireUIAnalytics(createAnalyticsEvent({}), 'moreResultsButton shown');
					filtersRef.current = filters;
				}

				actions.setIsLastResultMarkerBelowFold(isLastResultBelowFold);
			},
			{
				root: scrollElement,
				threshold: 0.5,
			},
		);
		const markerElement = markerRef.current;

		if (markerElement && markerRef.current) {
			observer.observe(markerRef.current);
		}

		return () => {
			if (markerElement) observer.unobserve(markerElement);
			actions.setIsLastResultMarkerBelowFold(false);
		};
	}, [scrollElement, actions, createAnalyticsEvent, filters]);

	return <Marker role="presentation" ref={markerRef} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Marker = styled.hr({
	margin: 0,
	border: 'none',
});
